import React, { useCallback, useEffect, useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	Collapse,
	LinearProgress,
	Paper,
	Stack,
	Step,
	StepLabel,
	Stepper,
	Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import { GlobalState } from "context/GlobalContext";
import { AuthState } from "context/AuthContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { cardSx, primaryButtonSx, SectionCard } from "../sharedStyles";

const POLL_INTERVAL_MS = 5000;

const STATUS_CHIP_COLORS = {
	idle: "default",
	started: "info",
	running: "info",
	completed: "success",
	failed: "error",
};

const STATUS_ICON_COLORS = {
	idle: "#757575",
	started: "#0288d1",
	running: "#0288d1",
	completed: "#2e7d32",
	failed: "#d32f2f",
};

const WORKFLOW_STEPS = ["Upload CSV", "Save to BigQuery", "Apply Offers"];

const STATUS_LABELS = {
	idle: "Idle",
	started: "In Progress",
	running: "In Progress",
	completed: "Completed",
	failed: "Failed",
};

const STEP_LABELS = {
	starting: "Starting",
	loading_sale_data: "Loading sale data",
	loading_discount_schemes: "Loading discount schemes",
	loading_offers: "Loading offers",
	building_offer_file: "Building offer file",
	matching_offers: "Matching offers",
	applying_offers: "Applying offers",
	preparing_output: "Preparing output",
	saving_results: "Saving results",
	saving_to_bq: "Saving results",
	completed: "Completed",
	failed: "Failed",
};

function formatStatusLabel(status) {
	const key = String(status || "").trim().toLowerCase();
	return STATUS_LABELS[key] || "In Progress";
}

function formatStepLabel(step) {
	const key = String(step || "")
		.trim()
		.toLowerCase()
		.replace(/\s+/g, "_");
	if (!key) return "Waiting to start";
	if (STEP_LABELS[key]) return STEP_LABELS[key];
	return key
		.replace(/apply_lfs_offers/g, "offer application")
		.replace(/_/g, " ")
		.replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatUserMessage(message) {
	if (!message) return "";
	return String(message)
		.replace(/apply_lfs_offers/gi, "Offer application")
		.replace(/apply_offers/gi, "Offer application")
		.replace(/lfs sale data/gi, "Sale data")
		.replace(/\s+/g, " ")
		.trim();
}

const ACCENT = {
	primary: { main: "#212121", soft: "rgba(33, 33, 33, 0.06)", border: "rgba(33, 33, 33, 0.12)" },
	info: { main: "#0288d1", soft: "rgba(2, 136, 209, 0.08)", border: "rgba(2, 136, 209, 0.18)" },
	success: { main: "#2e7d32", soft: "rgba(46, 125, 50, 0.08)", border: "rgba(46, 125, 50, 0.18)" },
	error: { main: "#d32f2f", soft: "rgba(211, 47, 47, 0.08)", border: "rgba(211, 47, 47, 0.18)" },
};

const spinIconSx = {
	animation: "lfsSaleSpin 1.2s linear infinite",
	"@keyframes lfsSaleSpin": {
		from: { transform: "rotate(0deg)" },
		to: { transform: "rotate(360deg)" },
	},
};

function formatStatValue(value) {
	if (value === null || value === undefined || value === "") return "-";
	return String(value);
}

function StatCard({ icon, label, value, accent = "primary" }) {
	const colors = ACCENT[accent] || ACCENT.primary;

	return (
		<Paper
			elevation={0}
			sx={{
				p: 2,
				borderRadius: 2.5,
				border: `1px solid ${colors.border}`,
				bgcolor: colors.soft,
				height: "100%",
			}}
		>
			<Stack direction="row" spacing={1.5} alignItems="flex-start">
				<Box
					sx={{
						width: 40,
						height: 40,
						borderRadius: 2,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						bgcolor: colors.border,
						color: colors.main,
						flexShrink: 0,
					}}
				>
					{icon}
				</Box>
				<Box minWidth={0}>
					<Typography variant="caption" color="text.secondary" fontWeight={600}>
						{label}
					</Typography>
					<Typography variant="h6" fontWeight={700} lineHeight={1.2}>
						{formatStatValue(value)}
					</Typography>
				</Box>
			</Stack>
		</Paper>
	);
}

function renderStatusIcon(status, isApplying) {
	if (status === "completed") return <CheckCircleOutlineIcon />;
	if (status === "failed") return <ErrorOutlineIcon />;
	if (isApplying) return <AutorenewIcon sx={spinIconSx} />;
	return <InfoOutlinedIcon />;
}

function SaleDataWorking() {
	const { setSnackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();
	const isAdmin = Boolean(user?.role?.ADMIN || user?.role?.["ADMIN"]);

	const [selectedFile, setSelectedFile] = useState(null);
	const [uploadedFileName, setUploadedFileName] = useState("");
	const [uploadResult, setUploadResult] = useState(null);
	const [statusError, setStatusError] = useState("");
	const [applyResult, setApplyResult] = useState(null);
	const [isDragOver, setIsDragOver] = useState(false);
	const [downloadingSample, setDownloadingSample] = useState(false);
	const [forceStarting, setForceStarting] = useState(false);
	const [jobStatus, setJobStatus] = useState({
		status: "idle",
		progress_percent: 0,
		current_step: null,
		message: null,
	});

	const pollTimeoutRef = useRef(null);
	const isPollingRef = useRef(false);
	const startPollingRef = useRef(() => {});
	const fileInputRef = useRef(null);

	const clearPollTimeout = useCallback(() => {
		if (pollTimeoutRef.current) {
			clearTimeout(pollTimeoutRef.current);
			pollTimeoutRef.current = null;
		}
	}, []);

	const showSnack = useCallback(
		(message, severity = "info") => {
			setSnackBarOpen(true);
			setSnackBar((prev) => ({
				...prev,
				message,
				severity,
				anchorOrigin: { horizontal: "center", vertical: "top" },
			}));
		},
		[setSnackBar, setSnackBarOpen]
	);

	const fetchApplyStatus = useCallback(async () => {
		const res = await pythonVmInstance({
			url: "/lfs-payment-working/apply-offers/status",
			method: "GET",
		});
		return res.data;
	}, []);

	const handleStatusResponse = useCallback((data) => {
		const snapshot = data?.job_status || {};
		const applyStatus = data?.apply_offers_status || snapshot.status || "idle";

		setJobStatus({
			status: applyStatus,
			progress_percent: data?.progress_percent ?? snapshot.progress_percent ?? 0,
			current_step: snapshot.current_step,
			message: snapshot.message || data?.message,
			last_started_at: snapshot.last_started_at,
			last_completed_at: snapshot.last_completed_at,
			last_error: snapshot.last_error,
		});

		if (applyStatus === "completed") {
			setApplyResult(snapshot.last_result || data);
			setStatusError("");
			return "completed";
		}

		if (applyStatus === "failed") {
			setStatusError(
				formatUserMessage(data?.message || snapshot.last_error) ||
					"Offer application failed"
			);
			return "failed";
		}

		if (applyStatus === "running" || applyStatus === "started") {
			return "running";
		}

		return "idle";
	}, []);

	const startPolling = useCallback(() => {
		if (isPollingRef.current) return;
		isPollingRef.current = true;

		const poll = async () => {
			try {
				const data = await fetchApplyStatus();
				const state = handleStatusResponse(data);

				if (state === "running") {
					pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
					return;
				}

				isPollingRef.current = false;
				// Success is shown in the results Alert below — avoid a duplicate snack.
				if (state === "failed") {
					showSnack(
						formatUserMessage(data?.message) || "Offer application failed",
						"error"
					);
				}
			} catch (error) {
				isPollingRef.current = false;
				const message =
					formatUserMessage(error?.response?.data?.message) ||
					"Failed to fetch offer application status";
				setStatusError(message);
				showSnack(message, "error");
			}
		};

		poll();
	}, [fetchApplyStatus, handleStatusResponse, showSnack]);

	startPollingRef.current = startPolling;

	useEffect(() => {
		startPollingRef.current();
		return () => {
			clearPollTimeout();
			isPollingRef.current = false;
		};
	}, [clearPollTimeout]);

	const handleDownloadSampleCsv = async () => {
		try {
			setDownloadingSample(true);
			const res = await pythonVmInstance.get(
				"/lfs-payment-working/sale-data/sample-csv",
				{ responseType: "blob" }
			);

			const contentType = res.headers?.["content-type"] || "";
			if (contentType.includes("application/json")) {
				const text = await res.data.text();
				const errorData = JSON.parse(text);
				throw new Error(errorData.message || "Failed to download sample CSV");
			}

			const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", "lfs_sale_data_sample.csv");
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
			showSnack("Sample CSV downloaded", "success");
		} catch (error) {
			let message = "Failed to download sample CSV";
			const data = error?.response?.data;
			if (data instanceof Blob) {
				try {
					const text = await data.text();
					const parsed = JSON.parse(text);
					message = parsed.message || message;
				} catch (_parseError) {
					message = error?.message || message;
				}
			} else if (data?.message) {
				message = data.message;
			} else if (error?.message) {
				message = error.message;
			}
			showSnack(message, "error");
		} finally {
			setDownloadingSample(false);
		}
	};

	const handleForceStartOffers = async () => {
		try {
			setForceStarting(true);
			setStatusError("");
			setApplyResult(null);

			const res = await pythonVmInstance.post(
				"/lfs-payment-working/apply-offers?force=true"
			);

			setJobStatus({
				status: res.data?.apply_offers_status || "running",
				progress_percent: res.data?.job_status?.progress_percent ?? 0,
				current_step: res.data?.job_status?.current_step || "starting",
				message: "Offer application force started.",
			});
			showSnack("Offer application force started.", "success");
			isPollingRef.current = false;
			startPolling();
		} catch (error) {
			const message =
				formatUserMessage(error?.response?.data?.message) ||
				"Failed to force start offer application";
			setStatusError(message);
			showSnack(message, "error");
		} finally {
			setForceStarting(false);
		}
	};

	const acceptFile = (file) => {
		if (!file) return;
		if (!file.name.toLowerCase().endsWith(".csv")) {
			setSelectedFile(null);
			setUploadedFileName("");
			showSnack("Please select a CSV file.", "warning");
			return;
		}
		setSelectedFile(file);
		setUploadedFileName(file.name);
		setUploadResult(null);
		setStatusError("");
		setApplyResult(null);
	};

	const handleFileSelect = (event) => {
		acceptFile(event.target.files?.[0] || null);
		event.target.value = "";
	};

	const handleDrop = (event) => {
		event.preventDefault();
		setIsDragOver(false);
		acceptFile(event.dataTransfer.files?.[0] || null);
	};

	const handleUploadSaleData = async () => {
		if (!selectedFile) {
			showSnack("Please select a sale data CSV file.", "warning");
			return;
		}

		const form = new FormData();
		form.append("file", selectedFile);

		try {
			setLoadingOpen(true);
			setStatusError("");
			setApplyResult(null);

			const res = await pythonVmInstance.post(
				"/lfs-payment-working/upload-sale-data",
				form,
				{ headers: { "Content-Type": "multipart/form-data" } }
			);

			setUploadResult(res.data);
			setSelectedFile(null);
			setUploadedFileName("");

			const applyStatus = res.data?.apply_offers_status;
			if (applyStatus === "started" || applyStatus === "running") {
				setJobStatus({
					status: applyStatus,
					progress_percent: res.data?.job_status?.progress_percent ?? 0,
					current_step: res.data?.job_status?.current_step || "starting",
					message: "Sale data uploaded. Offer application started.",
				});
				showSnack("Sale data uploaded. Offer application started.", "success");
				startPolling();
			} else {
				showSnack("Sale data uploaded successfully", "success");
			}
		} catch (error) {
			const data = error?.response?.data || {};
			const message =
				formatUserMessage(data.message) || "Failed to upload sale data";
			const missingFields = Array.isArray(data.missing_fields)
				? data.missing_fields
				: [];

			setStatusError(message);
			setUploadResult(
				missingFields.length > 0
					? {
							validation_error: true,
							missing_fields: missingFields,
							found_columns: data.found_columns,
						}
					: null
			);
			showSnack(message, "error");
		} finally {
			setLoadingOpen(false);
		}
	};

	const isApplying =
		jobStatus.status === "running" || jobStatus.status === "started";
	const showProgress =
		isApplying || jobStatus.status === "completed" || jobStatus.status === "failed";

	const activeStep =
		jobStatus.status === "completed"
			? 3
			: isApplying
				? 2
				: uploadResult && !uploadResult.validation_error
					? 1
					: 0;

	const statusChipColor = STATUS_CHIP_COLORS[jobStatus.status] || "default";
	const statusIconColor = STATUS_ICON_COLORS[jobStatus.status] || STATUS_ICON_COLORS.idle;

	return (
		<Box>
			<Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, sm: 2.5 }, mb: 3 }}>
				<Stepper activeStep={activeStep} alternativeLabel>
					{WORKFLOW_STEPS.map((label) => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>
			</Paper>

			<SectionCard
				title="Upload Sale Data"
				icon={<CloudUploadOutlinedIcon color="primary" />}
			>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Expected columns: STORE CODE, DISTRIBUTORS, G-SHORT NAME, G-CODE,
					BILLNO, BILLDATE, MRP, BILLQTY, MRP VALUE, DISCOUNT, NETAMT, BARCODE.
					Brand and item details (division, style, HSN, fabric, season, COGS)
					are filled from item master during Apply Offers.
				</Typography>
				<Box
					onDragOver={(e) => {
						e.preventDefault();
						setIsDragOver(true);
					}}
					onDragLeave={() => setIsDragOver(false)}
					onDrop={handleDrop}
					onClick={() => !isApplying && fileInputRef.current?.click()}
					sx={{
						border: "2px dashed",
						borderColor: isDragOver
							? "#212121"
							: selectedFile
								? "rgba(46, 125, 50, 0.5)"
								: "#e0e0e0",
						borderRadius: 3,
						p: { xs: 3, sm: 4 },
						textAlign: "center",
						cursor: isApplying ? "not-allowed" : "pointer",
						bgcolor: isDragOver
							? "rgba(33, 33, 33, 0.04)"
							: selectedFile
								? "rgba(46, 125, 50, 0.04)"
								: "rgba(0, 0, 0, 0.02)",
						transition: "all 0.2s ease",
						"&:hover": isApplying
							? {}
							: {
									borderColor: "#212121",
									bgcolor: "rgba(33, 33, 33, 0.04)",
								},
					}}
				>
					<input
						ref={fileInputRef}
						type="file"
						hidden
						accept=".csv"
						onChange={handleFileSelect}
						disabled={isApplying}
					/>
					<Box
						sx={{
							width: 56,
							height: 56,
							mx: "auto",
							mb: 1.5,
							borderRadius: "50%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							bgcolor: "rgba(33, 33, 33, 0.08)",
							color: "#212121",
						}}
					>
						{selectedFile ? (
							<InsertDriveFileOutlinedIcon fontSize="large" />
						) : (
							<CloudUploadOutlinedIcon fontSize="large" />
						)}
					</Box>
					<Typography variant="subtitle1" fontWeight={600}>
						{selectedFile ? uploadedFileName : "Drag & drop your CSV here"}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						{selectedFile
							? "Click to choose a different file"
							: "or click to browse from your computer"}
					</Typography>
				</Box>

				<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={2.5}>
					<Button
						variant="contained"
						size="large"
						fullWidth
						onClick={handleUploadSaleData}
						disabled={!selectedFile || isApplying}
						startIcon={<CloudUploadOutlinedIcon />}
						sx={{
							py: 1.25,
							borderRadius: 2,
							fontWeight: 700,
							boxShadow: "0 8px 20px rgba(33, 33, 33, 0.22)",
							...primaryButtonSx,
						}}
					>
						Upload & Apply Offers
					</Button>

					<Button
						variant="outlined"
						size="large"
						fullWidth
						onClick={handleDownloadSampleCsv}
						disabled={downloadingSample}
						startIcon={<DownloadOutlinedIcon />}
						sx={{
							py: 1.1,
							borderRadius: 2,
							fontWeight: 600,
						}}
					>
						{downloadingSample ? "Downloading..." : "Download Sample CSV"}
					</Button>
				</Stack>

				{isAdmin ? (
					<Button
						variant="contained"
						color="warning"
						size="large"
						fullWidth
						onClick={handleForceStartOffers}
						disabled={forceStarting || isApplying}
						startIcon={<BoltOutlinedIcon />}
						sx={{
							mt: 1.5,
							py: 1.1,
							borderRadius: 2,
							fontWeight: 700,
						}}
					>
						{forceStarting ? "Starting..." : "Force Start Offers"}
					</Button>
				) : null}

				{uploadResult?.validation_error ? (
					<Alert severity="error" sx={{ mt: 2.5, borderRadius: 2 }}>
						<Typography variant="body2" fontWeight={700} gutterBottom>
							{statusError}
						</Typography>
						<Typography variant="body2" sx={{ mb: 1 }}>
							Each required field must match one of the accepted column names:
						</Typography>
						<Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
							{(uploadResult.missing_fields || []).map((field) => (
								<Typography key={field.field} component="li" variant="body2">
									<strong>{field.field}</strong>:{" "}
									{(field.accepted_columns || []).join(", ")}
								</Typography>
							))}
						</Stack>
					</Alert>
				) : null}
			</SectionCard>

			{uploadResult && !uploadResult.validation_error ? (
				<Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, sm: 3 }, mb: 3 }}>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						mb={2}
						flexWrap="wrap"
						gap={1}
					>
						<Typography variant="h6" fontWeight={700}>
							Upload Summary
						</Typography>
						<Chip
							label="Uploaded"
							color="success"
							size="small"
							sx={{ fontWeight: 700 }}
						/>
					</Stack>

					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "1fr",
								sm: "repeat(3, minmax(0, 1fr))",
							},
							gap: 1.5,
						}}
					>
						<Box
							sx={{
								p: 2,
								borderRadius: 2,
								border: "1px solid #e8e8e8",
								bgcolor: "#fafafa",
								minWidth: 0,
							}}
						>
							<Typography variant="caption" color="text.secondary" fontWeight={600}>
								Rows Uploaded
							</Typography>
							<Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, wordBreak: "break-word" }}>
								{formatStatValue(uploadResult.rows_uploaded)}
							</Typography>
						</Box>
						<Box
							sx={{
								p: 2,
								borderRadius: 2,
								border: "1px solid #e8e8e8",
								bgcolor: "#fafafa",
								minWidth: 0,
							}}
						>
							<Typography variant="caption" color="text.secondary" fontWeight={600}>
								Keys Replaced
							</Typography>
							<Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, wordBreak: "break-word" }}>
								{formatStatValue(uploadResult.keys_deleted)}
							</Typography>
						</Box>
						<Box
							sx={{
								p: 2,
								borderRadius: 2,
								border: "1px solid #e8e8e8",
								bgcolor: "#fafafa",
								minWidth: 0,
							}}
						>
							<Typography variant="caption" color="text.secondary" fontWeight={600}>
								Offer Status
							</Typography>
							<Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, wordBreak: "break-word" }}>
								{formatStatusLabel(uploadResult.apply_offers_status)}
							</Typography>
						</Box>
					</Box>
				</Paper>
			) : null}

			<Collapse in={showProgress}>
				<Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, sm: 3 }, mt: 3 }}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						justifyContent="space-between"
						alignItems={{ xs: "flex-start", sm: "center" }}
						spacing={1.5}
						mb={2.5}
					>
						<Stack direction="row" alignItems="center" spacing={1.5}>
							<Box
								sx={{
									width: 44,
									height: 44,
									borderRadius: 2,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									bgcolor: `${statusIconColor}22`,
									color: statusIconColor,
								}}
							>
								{renderStatusIcon(jobStatus.status, isApplying)}
							</Box>
							<Box>
								<Typography variant="h6" fontWeight={700}>
									Offer Application
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{formatStepLabel(jobStatus.current_step)}
								</Typography>
							</Box>
						</Stack>
						<Chip
							label={formatStatusLabel(jobStatus.status)}
							color={statusChipColor}
							sx={{ fontWeight: 700 }}
						/>
					</Stack>

					<Box sx={{ mb: 2 }}>
						<Stack direction="row" justifyContent="space-between" mb={0.75}>
							<Typography variant="body2" color="text.secondary" fontWeight={500}>
								Progress
							</Typography>
							<Typography variant="body2" fontWeight={700}>
								{jobStatus.progress_percent ?? 0}%
							</Typography>
						</Stack>
						<LinearProgress
							variant="determinate"
							value={jobStatus.progress_percent ?? 0}
							sx={{
								height: 10,
								borderRadius: 5,
								bgcolor: "rgba(33, 33, 33, 0.08)",
								"& .MuiLinearProgress-bar": { borderRadius: 5 },
							}}
						/>
					</Box>

					{formatUserMessage(jobStatus.message) &&
					jobStatus.status !== "completed" ? (
						<Alert
							severity={jobStatus.status === "failed" ? "error" : "info"}
							icon={false}
							sx={{ borderRadius: 2, mb: 2 }}
						>
							<Typography variant="body2">
								{formatUserMessage(jobStatus.message)}
							</Typography>
						</Alert>
					) : null}

					{statusError ? (
						<Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
							{statusError}
						</Alert>
					) : null}

					{applyResult ? (
						<Box>
							<Alert
								severity="success"
								icon={<CheckCircleOutlineIcon />}
								sx={{ borderRadius: 2, mb: 2 }}
							>
								Offer application completed successfully.
							</Alert>
							<Stack
								direction={{ xs: "column", sm: "row" }}
								spacing={1.5}
								alignItems="stretch"
							>
								<Box flex={1}>
									<StatCard
										icon={<TableChartOutlinedIcon fontSize="small" />}
										label="Sale Rows"
										value={applyResult.sale_rows}
									/>
								</Box>
								<Box flex={1}>
									<StatCard
										icon={<LocalOfferOutlinedIcon fontSize="small" />}
										label="Offer Rows"
										value={applyResult.offer_rows}
										accent="info"
									/>
								</Box>
								<Box flex={1}>
									<StatCard
										icon={<StorageOutlinedIcon fontSize="small" />}
										label="Output Rows"
										value={applyResult.output_rows ?? applyResult.rows_saved}
										accent="success"
									/>
								</Box>
							</Stack>
						</Box>
					) : null}
				</Paper>
			</Collapse>
		</Box>
	);
}

export default SaleDataWorking;
